import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Anchor, ArrowLeft, CheckCircle2, XCircle, Copy, BarChart3, BookOpen } from "lucide-react";
import { toast } from "sonner";

export default function SessionResultsPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/sessions/${sessionId}/report`);
        setReport(data);
      } catch {
        toast.error("Report not available yet");
      } finally {
        setLoading(false);
      }
    })();
  }, [sessionId]);

  const scoreClass = (score) => {
    if (score >= 6) return { text: "cop-score-pass", bg: "cop-score-bg-pass", label: "PASS" };
    if (score >= 4) return { text: "cop-score-mid", bg: "cop-score-bg-mid", label: "FAIL" };
    return { text: "cop-score-fail", bg: "cop-score-bg-fail", label: "FAIL" };
  };

  const handleShare = () => {
    if (!report) return;
    const sc = scoreClass(report.overall_score);
    const text = `CalmOceanPrep - MMD 2nd Mate Oral Practice\n\nFunction: ${report.function_name || "N/A"}\nScore: ${report.overall_score}/10 - ${sc.label}\n\nStrong: ${report.strong_topics}\nWeak: ${report.weak_topics}\n\nPractice at: ${window.location.origin}`;
    navigator.clipboard.writeText(text);
    toast.success("Result copied to clipboard! Share it on WhatsApp.");
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!report) return (
    <div className="flex flex-col items-center justify-center min-h-screen gap-4 px-4">
      <p className="text-muted-foreground">Report not available. The session may still be in progress.</p>
      <Button asChild><Link to="/dashboard">Back to Dashboard</Link></Button>
    </div>
  );

  const sc = scoreClass(report.overall_score);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="cop-glass border-b px-4 py-3 flex items-center gap-3" data-testid="results-header">
        <Button variant="ghost" size="sm" className="p-1" onClick={() => navigate("/dashboard")} data-testid="results-back-btn">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <Anchor className="h-4 w-4 text-primary" />
        <span className="text-sm font-medium">Session Results</span>
      </header>

      <main className="container mx-auto px-4 max-w-lg py-6 space-y-4" data-testid="results-main">
        {/* Score Card */}
        <Card className={`border-2 ${sc.bg}`} data-testid="score-card">
          <CardContent className="p-6 text-center">
            <div className="mb-3">
              {report.overall_score >= 6 ? (
                <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto" />
              ) : (
                <XCircle className="h-12 w-12 text-red-500 mx-auto" />
              )}
            </div>
            <p className={`text-5xl font-bold mb-2 ${sc.text}`} style={{ fontFamily: 'Manrope' }} data-testid="score-display">
              {report.overall_score}/10
            </p>
            <Badge variant={report.overall_score >= 6 ? "default" : "destructive"} className="text-sm px-3 py-1" data-testid="pass-fail-badge">
              {sc.label}
            </Badge>
            <p className="text-xs text-muted-foreground mt-2">{report.function_name}</p>
          </CardContent>
        </Card>

        {/* Strong Areas */}
        <Card className="border" data-testid="strong-areas-card">
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold flex items-center gap-2 mb-2 text-green-700">
              <CheckCircle2 className="h-4 w-4" /> Strong Areas
            </h3>
            <p className="text-sm text-muted-foreground">{report.strong_topics}</p>
          </CardContent>
        </Card>

        {/* Weak Areas */}
        <Card className="border" data-testid="weak-areas-card">
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold flex items-center gap-2 mb-2 text-red-600">
              <XCircle className="h-4 w-4" /> Weak Areas
            </h3>
            <p className="text-sm text-muted-foreground">{report.weak_topics}</p>
          </CardContent>
        </Card>

        {/* Detailed Feedback */}
        <Card className="border" data-testid="feedback-card">
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold flex items-center gap-2 mb-2">
              <BarChart3 className="h-4 w-4 text-primary" /> Detailed Feedback
            </h3>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">{report.detailed_feedback}</p>
          </CardContent>
        </Card>

        {/* Study Recommendations */}
        <Card className="border" data-testid="recommendations-card">
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold flex items-center gap-2 mb-2">
              <BookOpen className="h-4 w-4 text-amber-600" /> Study Recommendations
            </h3>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">{report.study_recommendations}</p>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex flex-col gap-3 pt-2">
          <Button onClick={() => navigate("/dashboard")} data-testid="practice-again-btn">
            Practice Again
          </Button>
          <Button variant="outline" onClick={handleShare} data-testid="share-result-btn">
            <Copy className="h-4 w-4 mr-2" /> Share Result
          </Button>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/dashboard">Back to Dashboard</Link>
          </Button>
        </div>
      </main>
    </div>
  );
}
